import { Trans, useLingui } from "@lingui/react/macro"
import type { CallActivityOutcome } from "@/types/v2/conversation"
import type { VoiceCallRecord } from "@/types/v2/voice"
import { useTranscriptViewer } from "./transcript-viewer-context"
import { useDrawerNavContext } from "@/components/v2/drawer/drawer-nav-context"
import { pushDrawer } from "@/lib/drawer-trail"

// Shared presentation for a logged voice call in any
// timeline-style feed (CON-144). The kind-based
// `ActivityFeed` (deal show + slideover, contact show +
// slideover) renders call rows from these pieces so the
// icon tint, outcome tone, duration, and "Analyzing call…"
// affordance stay identical across every surface.

// Semantic status colors are intentionally hardcoded
// (CLAUDE.md): they carry meaning independent of the theme.
// Labels are resolved via useOutcomeLabel() inside components
// so they go through Lingui's translation pipeline.
export const CALL_OUTCOME_TONE: Record<
  CallActivityOutcome,
  { text: string; dot: string }
> = {
  connected: {
    text: "text-[#5C8A4F]",
    dot: "bg-green-500",
  },
  missed: {
    text: "text-[#B86A2D]",
    dot: "bg-yellow-500",
  },
  declined: {
    text: "text-[#9A938A]",
    dot: "bg-[#9A938A]",
  },
  failed: {
    text: "text-[#9A938A]",
    dot: "bg-[#9A938A]",
  },
}

function useOutcomeLabel(outcome: CallActivityOutcome): string {
  const { t } = useLingui()
  const labels: Record<CallActivityOutcome, string> = {
    connected: t`Connected`,
    missed: t`Missed`,
    declined: t`Declined`,
    failed: t`Failed`,
  }
  return labels[outcome]
}

// Phone glyph for call rows, tinted by outcome so the
// timeline reads at a glance (green = connected, amber =
// missed, muted = declined / failed). Direction drives the
// aria-label so screen readers distinguish inbound from
// outbound calls.
export function CallRowIcon({
  outcome,
  direction,
}: {
  outcome: CallActivityOutcome | null
  direction?: "inbound" | "outbound"
}) {
  const stroke =
    outcome === "connected"
      ? "#5C8A4F"
      : outcome === "missed"
        ? "#B86A2D"
        : "#7A746B"
  const { t } = useLingui()
  const label =
    direction === "inbound" ? t`Incoming call` : t`Outgoing call`
  return (
    <div
      // Shimmer leaf: fill the whole circle uniformly so the
      // tinted background doesn't leak around the glyph as a
      // ring during the activity-feed loading shimmer.
      data-shimmer-no-children
      className="flex items-center justify-center shrink-0 rounded-full size-6 bg-[#E7EEF2]"
      role="img"
      aria-label={label}
    >
      <svg
        width="11"
        height="11"
        viewBox="0 0 24 24"
        fill="none"
        stroke={stroke}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
      </svg>
    </div>
  )
}

// Call-row body: an outcome + duration meta line, then the
// AI summary once it lands. While the post-call analysis is
// genuinely still running (`analysisInFlight`, computed
// server-side — see `VoiceCall#analysis_in_flight?`) and no
// summary is on the record yet, show a subtle "Analyzing
// call…" affordance instead of an empty gap. Once analysis
// terminates — failed, or stranded with a transcript that
// never progressed (CON-198) — drop the affordance entirely:
// the row already shows outcome + duration, so no summary
// just means no summary, never a perpetual spinner.
export function CallRowBody({
  outcome,
  durationLabel,
  body,
  analysisInFlight = null,
  voiceCall = null,
}: {
  outcome: CallActivityOutcome | null
  durationLabel: string | null
  body: string | null
  // Server-computed "analysis still running" gate (CON-198).
  // null (older payloads) reads as not analyzing.
  analysisInFlight?: boolean | null
  // The full call record — present (with a ready transcript)
  // only on call rows shown inside a `TranscriptViewerProvider`
  // (deal show / contact show / inbox). Drives the "View
  // transcript" trigger. null elsewhere → no trigger.
  voiceCall?: VoiceCallRecord | null
}) {
  const tone = outcome ? CALL_OUTCOME_TONE[outcome] : null
  const outcomeLabel = useOutcomeLabel(outcome ?? "failed")
  const analyzing = !body && analysisInFlight === true

  // Two ways to open the call from a timeline row:
  //   - Inside a drawer entry body → push a `call:<id>` entry
  //     onto the slideover trail so the CallDetailPanel opens
  //     WITHIN the drawer (the bug fix — the floating rail
  //     would otherwise render behind the drawer).
  //   - On a show page / inbox thread (no drawer) → open the
  //     floating transcript viewer (rail/overlay).
  // `useDrawerNavContext` is the "am I inside a drawer?" signal;
  // `useTranscriptViewer` is null when no floating viewer is
  // mounted (isolated tests). The trigger shows when EITHER
  // path can open the call.
  const drawerNav = useDrawerNavContext()
  const transcriptViewer = useTranscriptViewer()
  const canOpenCall =
    voiceCall != null &&
    voiceCall.transcriptStatus === "ready" &&
    (drawerNav != null || transcriptViewer != null)

  const openCall = () => {
    if (!voiceCall) return
    if (drawerNav) {
      pushDrawer("call", voiceCall.id, drawerNav)
      return
    }
    transcriptViewer?.openTranscript(voiceCall)
  }

  return (
    <div className="flex flex-col gap-1.5">
      {tone || durationLabel ? (
        <div className="flex items-center gap-1.5 text-[12px] leading-4">
          {tone ? (
            <>
              <span
                aria-hidden="true"
                className={`size-1.5 rounded-full ${tone.dot}`}
              />
              <span className={`font-medium ${tone.text}`}>
                {outcomeLabel}
              </span>
            </>
          ) : null}
          {durationLabel ? (
            <span className="text-[#9A938A]">
              {tone ? "· " : ""}
              {durationLabel}
            </span>
          ) : null}
        </div>
      ) : null}
      {body ? (
        <div className="text-[#5C544A] text-[13px] leading-[18px]">
          {body}
        </div>
      ) : analyzing ? (
        <div
          data-testid="call-row-analyzing"
          className="inline-flex items-center gap-1 text-brand text-[12px] leading-4"
        >
          <span aria-hidden="true">✨</span>
          <span><Trans>Analyzing call…</Trans></span>
        </div>
      ) : null}
      {canOpenCall ? (
        <button
          type="button"
          data-testid="call-row-open-call"
          onClick={openCall}
          className="inline-flex w-fit items-center gap-1 text-brand text-[12px] leading-4 font-medium hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand rounded-sm"
        >
          <Trans>Open call</Trans>
        </button>
      ) : null}
    </div>
  )
}
